import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { deriveStatsFor } from '@dcc/rules/app/derivedStats.ts';
import { usePlaySheet } from './usePlaySheet.ts';
import { VitalsHeader } from './VitalsHeader.tsx';
import { PlayHotlistPanel } from './PlayHotlistTab.tsx';
import { PlayInventoryPanel, type ItemPowerHost } from './PlayInventoryTab.tsx';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';

/**
 * One live play snapshot for both Hotlist and Inventory.
 *
 * This is deliberately NOT two old tabs rendered next to each other. Those tabs
 * each used to own an in-memory Character snapshot; mounting both would let one
 * panel save while the other still held the older crawler. Sharing one
 * usePlaySheet controller keeps every Hotlist/Inventory action on the same
 * current state and mutation gate.
 */
export function PlayItemsTab(props: {
  services: AppServices;
  characterId: string;
  refreshToken?: number;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
  onOpenCombat?: (skillId: string, mode?: RollMode) => void;
  itemPowers?: ItemPowerHost;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate, props.refreshToken);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const effects = activeEffects(play.records.runtime);
  const effective = buildEffectiveCharacter(play.records.character, effects, (id) => contentRegistry.getItem(id));

  return (
    <div className="dcc-play">
      <VitalsHeader character={play.records.character} derived={deriveStatsFor(effective)} />
      <PlayHotlistPanel {...props} play={play} showVitals={false} onOpenCombat={props.onOpenCombat} />
      <PlayInventoryPanel {...props} play={play} showVitals={false} />
    </div>
  );
}
